import { useRouter } from "@shared/client/navigation";
import { useState } from "react";
import { BadgeEuro, Check, LoaderCircle, Mail, Plus, Star, Trash2, UserPlus } from "lucide-react";
import type {
  ClientRecord,
  ContactRecord,
  InvoiceRecord,
  MemberRecord,
  PlanRecord,
  SubscriptionRecord,
} from "@shared/lib/admin-db";
import type { ClientAgentCosts } from "@shared/lib/agent-costs";
import { formatCents, formatDate, formatDateTime, formatNumber, formatPercent } from "@shared/lib/format";
import { AdminChip, AdminEmpty, AdminPanel, StatCard, StatGroup } from "@shared/components/blocks";
import { Alert, AlertDescription } from "@shared/components/ui/alert";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shared/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@shared/components/ui/table";
import { Textarea } from "@shared/components/ui/textarea";

const ROLE_LABELS: Record<string, string> = {
  owner: "Propriétaire",
  admin: "Admin",
  member: "Membre",
  viewer: "Lecture seule",
};

const CLIENT_STATUSES = [
  { value: "lead", label: "Prospect" },
  { value: "trial", label: "Essai" },
  { value: "active", label: "Actif" },
  { value: "churned", label: "Perdu" },
];

const SUBSCRIPTION_STATUSES = [
  { value: "trialing", label: "Essai" },
  { value: "active", label: "Actif" },
  { value: "past_due", label: "Impayé" },
  { value: "cancelled", label: "Résilié" },
];

const INVOICE_STATUS_LABELS: Record<string, string> = {
  draft: "Brouillon",
  sent: "Envoyée",
  paid: "Payée",
  overdue: "En retard",
  void: "Annulée",
};

type ClientWithRelations = ClientRecord & {
  contacts: ContactRecord[];
  members: MemberRecord[];
  subscription: SubscriptionRecord | null;
  invoices: InvoiceRecord[];
};

async function api(path: string, method: string, body?: unknown) {
  const response = await fetch(path, {
    method,
    headers: { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error((data as { error?: string }).error || "Opération impossible.");
  return data;
}

/** Compteur discret accolé au titre d'un panneau. */
function PanelCount({ value }: { value: number }) {
  return <span className="ml-2 text-[12px] font-semibold text-muted-foreground tabular-nums">{value}</span>;
}

const inlineFormClass = "flex flex-wrap items-center gap-3 border-b bg-muted/40 px-4 py-3.5";

export function ClientDetail({
  agentCosts,
  client,
  plans,
}: {
  agentCosts: ClientAgentCosts;
  client: ClientWithRelations;
  plans: PlanRecord[];
}) {
  const router = useRouter();
  const [error, setError] = useState("");

  async function run(action: () => Promise<unknown>) {
    setError("");
    try {
      await action();
      router.refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Opération impossible.");
    }
  }

  return (
    <div className="grid gap-4.5">
      {error && (
        <Alert role="alert" variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <ClientInfoPanel client={client} onAction={run} />
      <AgentCostsPanel costs={agentCosts} mrrCents={client.subscription?.mrr_cents ?? 0} />
      <ContactsPanel client={client} onAction={run} />
      <MembersPanel client={client} onAction={run} />
      <BillingPanel client={client} onAction={run} plans={plans} />
    </div>
  );
}

const PROVIDER_LABELS: Record<string, string> = {
  deepseek: "DeepSeek",
  openai: "OpenAI",
  bedrock: "Amazon Bedrock",
  perplexity: "Perplexity",
  exa: "Exa",
  jina: "Jina",
  fetch: "Lecture directe",
};

function AgentCostsPanel({ costs, mrrCents }: { costs: ClientAgentCosts; mrrCents: number }) {
  const projectedMarginCents = mrrCents - costs.projectedMonthCostCents;
  const projectedMarginRate = mrrCents > 0 ? projectedMarginCents / mrrCents : null;

  return (
    <AdminPanel flush title="Coûts des agents IA">
      <StatGroup bordered={false} columns={4}>
        <StatCard
          label="Coût IA ce mois"
          note={`${formatNumber(costs.monthCalls)} appels · ${formatNumber(costs.monthInputTokens + costs.monthOutputTokens)} tokens`}
          value={formatCents(costs.monthCostCents)}
        />
        <StatCard
          label="Projection mensuelle"
          note="Au rythme du mois en cours"
          tint="amber"
          value={formatCents(costs.projectedMonthCostCents)}
        />
        <StatCard label="MRR client" note="Revenu mensuel renseigné" tint="teal" value={formatCents(mrrCents)} />
        <StatCard
          label="Marge après IA projetée"
          note={projectedMarginRate === null ? "MRR non renseigné" : `${formatPercent(projectedMarginRate)} du MRR`}
          tint={projectedMarginCents >= 0 ? "green" : "rose"}
          value={mrrCents > 0 ? formatCents(projectedMarginCents) : "—"}
        />
      </StatGroup>

      <div className="grid border-t lg:grid-cols-[1.2fr_.8fr]">
        <div className="border-b lg:border-r lg:border-b-0">
          <div className="border-b px-5 py-3 text-[12px] font-medium">
            Dépense conservée par fournisseur
          </div>
          {costs.breakdown.length === 0 ? (
            <AdminEmpty>Aucun appel IA enregistré pour les utilisateurs de ce client.</AdminEmpty>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="pl-5">Fournisseur</TableHead>
                  <TableHead className="text-right">Appels</TableHead>
                  <TableHead className="pr-5 text-right">Coût suivi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {costs.breakdown.map((item) => (
                  <TableRow key={item.provider}>
                    <TableCell className="pl-5 font-semibold">{PROVIDER_LABELS[item.provider] ?? item.provider}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatNumber(item.calls)}</TableCell>
                    <TableCell className="pr-5 text-right font-semibold tabular-nums">{formatCents(item.costCents)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
        <div className="grid content-start gap-3 px-5 py-4 text-[12px]">
          <div className="flex items-center justify-between gap-4">
            <span className="text-muted-foreground">30 derniers jours</span>
            <strong className="tabular-nums">{formatCents(costs.trailing30DaysCostCents)}</strong>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-muted-foreground">Historique conservé</span>
            <strong className="tabular-nums">{formatCents(costs.retainedCostCents)}</strong>
          </div>
          <p className="m-0 border-t pt-3 text-[12px] leading-relaxed text-muted-foreground">
            Coût API fournisseur lorsqu’il est renvoyé ({formatNumber(costs.providerReportedCalls)} appels), sinon
            calcul depuis les tokens réellement consommés et le tarif du modèle ({formatNumber(costs.rateCalculatedCalls)}
            appels). Les tarifs peuvent être alignés sur le contrat avec <code className="font-mono">AI_PRICING_EUR_JSON</code>.
            Les montants fournisseur en dollars utilisent <code className="font-mono">AI_USD_TO_EUR_RATE</code> pour
            la conversion. La marge soustrait uniquement l’IA du MRR, avant les autres charges.
          </p>
          {costs.unpricedCalls > 0 && (
            <p className="m-0 text-[12px] font-semibold text-destructive">
              {formatNumber(costs.unpricedCalls)} appel(s) sans tarif connu : le coût réel peut être supérieur.
            </p>
          )}
        </div>
      </div>
    </AdminPanel>
  );
}

type ActionRunner = (action: () => Promise<unknown>) => Promise<void>;

function ClientInfoPanel({ client, onAction }: { client: ClientWithRelations; onAction: ActionRunner }) {
  const [name, setName] = useState(client.name);
  const [website, setWebsite] = useState(client.website ?? "");
  const [industry, setIndustry] = useState(client.industry ?? "");
  const [status, setStatus] = useState<string>(client.status);
  const [notes, setNotes] = useState(client.notes ?? "");
  const [busy, setBusy] = useState(false);

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    await onAction(() =>
      api(`/api/admin/clients/${client.id}`, "PATCH", {
        name,
        website: website || null,
        industry: industry || null,
        status,
        notes: notes || null,
      }),
    );
    setBusy(false);
  }

  return (
    <AdminPanel title="Informations">
      <form className="grid gap-4 md:grid-cols-2" onSubmit={save}>
        <div className="grid gap-2">
          <Label htmlFor="client-name">Nom</Label>
          <Input id="client-name" onChange={(e) => setName(e.target.value)} required value={name} />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="client-website">Site web</Label>
          <Input id="client-website" inputMode="url" onChange={(e) => setWebsite(e.target.value)} value={website} />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="client-industry">Secteur</Label>
          <Input id="client-industry" onChange={(e) => setIndustry(e.target.value)} value={industry} />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="client-status">Statut</Label>
          <Select onValueChange={setStatus} value={status}>
            <SelectTrigger className="w-full" id="client-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CLIENT_STATUSES.map((item) => (
                <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-2 md:col-span-2">
          <Label htmlFor="client-notes">Notes internes</Label>
          <Textarea id="client-notes" onChange={(e) => setNotes(e.target.value)} rows={3} value={notes} />
        </div>
        <div className="md:col-span-2">
          <Button disabled={busy} type="submit">
            {busy ? <LoaderCircle className="animate-spin" /> : <Check />} Enregistrer
          </Button>
        </div>
      </form>
    </AdminPanel>
  );
}

function ContactsPanel({ client, onAction }: { client: ClientWithRelations; onAction: ActionRunner }) {
  const [adding, setAdding] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  async function addContact(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    await onAction(async () => {
      await api(`/api/admin/clients/${client.id}/contacts`, "POST", {
        full_name: fullName,
        email: email || undefined,
        job_title: jobTitle || undefined,
        phone: phone || undefined,
        is_primary: client.contacts.length === 0,
      });
      setFullName("");
      setEmail("");
      setJobTitle("");
      setPhone("");
      setAdding(false);
    });
    setBusy(false);
  }

  return (
    <AdminPanel
      action={
        <Button onClick={() => setAdding((v) => !v)} size="sm" type="button" variant="outline">
          <Plus /> Ajouter
        </Button>
      }
      flush
      title={`Contacts (${client.contacts.length})`}
    >
      {adding && (
        <form className={inlineFormClass} onSubmit={addContact}>
          <Input className="h-9 max-w-[220px] bg-background" onChange={(e) => setFullName(e.target.value)} placeholder="Nom complet" required value={fullName} />
          <Input className="h-9 max-w-[220px] bg-background" onChange={(e) => setEmail(e.target.value)} placeholder="E-mail" type="email" value={email} />
          <Input className="h-9 max-w-[200px] bg-background" onChange={(e) => setJobTitle(e.target.value)} placeholder="Fonction" value={jobTitle} />
          <Input className="h-9 max-w-[180px] bg-background" onChange={(e) => setPhone(e.target.value)} placeholder="Téléphone" value={phone} />
          <Button disabled={busy} size="sm" type="submit">
            {busy ? <LoaderCircle className="animate-spin" /> : <Plus />} Créer
          </Button>
        </form>
      )}

      {client.contacts.length === 0 ? (
        <AdminEmpty>Aucun contact enregistré pour ce client.</AdminEmpty>
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="pl-5">Contact</TableHead>
              <TableHead className="max-sm:hidden">Fonction</TableHead>
              <TableHead className="max-sm:hidden">E-mail</TableHead>
              <TableHead className="max-sm:hidden">Téléphone</TableHead>
              <TableHead className="pr-5" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {client.contacts.map((contact) => (
              <TableRow key={contact.id}>
                <TableCell className="pl-5">
                  <span className="flex flex-wrap items-center gap-2">
                    <strong className="font-semibold">{contact.full_name}</strong>
                    {contact.is_primary && (
                      <AdminChip className="gap-1" tone="role"><Star className="size-3" /> Principal</AdminChip>
                    )}
                  </span>
                </TableCell>
                <TableCell className="max-sm:hidden">{contact.job_title || "—"}</TableCell>
                <TableCell className="max-sm:hidden">
                  {contact.email ? (
                    <a className="inline-flex items-center gap-1.5 text-primary hover:underline" href={`mailto:${contact.email}`}>
                      <Mail className="size-3.5" />
                      {contact.email}
                    </a>
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell className="max-sm:hidden">{contact.phone || "—"}</TableCell>
                <TableCell className="pr-5">
                  <span className="flex items-center justify-end gap-1">
                    {!contact.is_primary && (
                      <Button
                        onClick={() => onAction(() => api(`/api/admin/contacts/${contact.id}`, "PATCH", { is_primary: true }))}
                        size="icon-sm"
                        title="Définir comme contact principal"
                        type="button"
                        variant="ghost"
                      >
                        <Star />
                      </Button>
                    )}
                    <Button
                      className="text-muted-foreground hover:text-destructive"
                      onClick={() => onAction(() => api(`/api/admin/contacts/${contact.id}`, "DELETE"))}
                      size="icon-sm"
                      title="Supprimer le contact"
                      type="button"
                      variant="ghost"
                    >
                      <Trash2 />
                    </Button>
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </AdminPanel>
  );
}

function MembersPanel({ client, onAction }: { client: ClientWithRelations; onAction: ActionRunner }) {
  const [adding, setAdding] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [busy, setBusy] = useState(false);

  async function addMember(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    await onAction(async () => {
      await api(`/api/admin/clients/${client.id}/members`, "POST", { email, role });
      setEmail("");
      setAdding(false);
    });
    setBusy(false);
  }

  return (
    <AdminPanel
      action={
        <Button onClick={() => setAdding((v) => !v)} size="sm" type="button" variant="outline">
          <UserPlus /> Rattacher
        </Button>
      }
      flush
      title={`Utilisateurs & droits (${client.members.length})`}
    >
      {adding && (
        <form className={inlineFormClass} onSubmit={addMember}>
          <Input
            className="h-9 max-w-[280px] bg-background"
            onChange={(e) => setEmail(e.target.value)}
            placeholder="E-mail d'un compte existant"
            required
            type="email"
            value={email}
          />
          <Select onValueChange={setRole} value={role}>
            <SelectTrigger className="h-9 min-w-[160px] bg-background text-xs font-semibold">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(ROLE_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button disabled={busy} size="sm" type="submit">
            {busy ? <LoaderCircle className="animate-spin" /> : <UserPlus />} Rattacher
          </Button>
        </form>
      )}

      {client.members.length === 0 ? (
        <AdminEmpty>Aucun utilisateur rattaché. Rattachez un compte existant par e-mail.</AdminEmpty>
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="pl-5">Utilisateur</TableHead>
              <TableHead>Rôle</TableHead>
              <TableHead className="max-sm:hidden">Dernière connexion</TableHead>
              <TableHead className="pr-5" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {client.members.map((member) => (
              <TableRow key={member.user_id}>
                <TableCell className="pl-5">
                  <strong className="block font-semibold">{member.full_name || member.email}</strong>
                  <small className="text-[12px] text-muted-foreground">{member.email}</small>
                </TableCell>
                <TableCell>
                  <Select
                    onValueChange={(value) =>
                      onAction(() =>
                        api(`/api/admin/clients/${client.id}/members`, "PATCH", { user_id: member.user_id, role: value }),
                      )
                    }
                    value={member.role}
                  >
                    <SelectTrigger aria-label={`Rôle de ${member.email}`} className="h-8 text-xs" size="sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ROLE_LABELS).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="whitespace-nowrap text-muted-foreground tabular-nums max-sm:hidden">
                  {member.last_sign_in_at ? formatDateTime(member.last_sign_in_at) : "Jamais"}
                </TableCell>
                <TableCell className="pr-5 text-right">
                  <Button
                    className="text-muted-foreground hover:text-destructive"
                    onClick={() =>
                      onAction(() => api(`/api/admin/clients/${client.id}/members`, "DELETE", { user_id: member.user_id }))
                    }
                    size="icon-sm"
                    title="Détacher du client"
                    type="button"
                    variant="ghost"
                  >
                    <Trash2 />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </AdminPanel>
  );
}

function BillingPanel({
  client,
  plans,
  onAction,
}: {
  client: ClientWithRelations;
  plans: PlanRecord[];
  onAction: ActionRunner;
}) {
  const subscription = client.subscription;
  const plan = plans.find((item) => item.id === subscription?.plan_id);
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [busyInvoice, setBusyInvoice] = useState(false);

  async function createInvoice(event: React.FormEvent) {
    event.preventDefault();
    setBusyInvoice(true);
    await onAction(async () => {
      await api("/api/admin/invoices", "POST", {
        client_id: client.id,
        amount_cents: Math.round(Number(amount.replace(",", ".")) * 100) || 0,
        description: description || undefined,
        status: "draft",
      });
      setAmount("");
      setDescription("");
    });
    setBusyInvoice(false);
  }

  return (
    <AdminPanel flush title="Facturation">
      <p className="m-0 border-b px-5 py-3.5 text-[13px] text-muted-foreground">
        {subscription ? (
          <>
            Abonnement Stripe d’un membre : plan {plan?.name ?? subscription.plan_id},{" "}
            {SUBSCRIPTION_STATUSES.find((item) => item.value === subscription.status)?.label.toLowerCase() ?? subscription.status},{" "}
            {formatCents(subscription.mrr_cents)} de MRR.
          </>
        ) : (
          "Aucun membre n’a d’abonnement payé. Les comptes s’abonnent eux-mêmes depuis leurs paramètres (Stripe)."
        )}
      </p>

      <div className="flex items-center justify-between gap-3 border-b px-5 py-3">
        <h3 className="m-0 text-[12px] font-medium">
          Factures
          <PanelCount value={client.invoices.length} />
        </h3>
      </div>

      <form className={inlineFormClass} onSubmit={createInvoice}>
        <Input
          className="h-9 max-w-[160px] bg-background"
          inputMode="decimal"
          onChange={(e) => setAmount(e.target.value)}
          placeholder="Montant (€)"
          required
          value={amount}
        />
        <Input
          className="h-9 max-w-[280px] bg-background"
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Description"
          value={description}
        />
        <Button disabled={busyInvoice} size="sm" type="submit">
          {busyInvoice ? <LoaderCircle className="animate-spin" /> : <BadgeEuro />} Créer la facture
        </Button>
      </form>

      {client.invoices.length === 0 ? (
        <AdminEmpty>Aucune facture pour ce client.</AdminEmpty>
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="pl-5">Numéro</TableHead>
              <TableHead className="text-right">Montant</TableHead>
              <TableHead className="max-sm:hidden">Émise le</TableHead>
              <TableHead className="pr-5">Statut</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {client.invoices.map((invoice) => (
              <TableRow key={invoice.id}>
                <TableCell className="pl-5">
                  <strong className="font-semibold">{invoice.number}</strong>
                  {invoice.description && (
                    <small className="block text-[12px] text-muted-foreground">{invoice.description}</small>
                  )}
                </TableCell>
                <TableCell className="text-right tabular-nums">{formatCents(invoice.amount_cents)}</TableCell>
                <TableCell className="whitespace-nowrap text-muted-foreground tabular-nums max-sm:hidden">
                  {formatDate(invoice.issued_at)}
                </TableCell>
                <TableCell className="pr-5">
                  <Select
                    onValueChange={(value) =>
                      onAction(() => api(`/api/admin/invoices/${invoice.id}`, "PATCH", { status: value }))
                    }
                    value={invoice.status}
                  >
                    <SelectTrigger aria-label={`Statut de la facture ${invoice.number}`} className="h-8 text-xs" size="sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(INVOICE_STATUS_LABELS).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </AdminPanel>
  );
}
